/**
 * Rec league profile social links: the single list of supported platforms,
 * shared by the settings form, the onboarding wizard, the profile renderer,
 * and any server code that needs to enumerate the `*_url` columns.
 *
 * Pure module — keep it free of model/React imports so unit tests and server
 * code can import it directly. Icons live in
 * `web/components/icons/socialLinkIcons.ts`; the mobile mirror is
 * `mobile/lib/util/recLeagueSocialLinks.ts` (keep the two in sync).
 */

export type SocialLinkKey =
  | "instagram_url"
  | "tiktok_url"
  | "youtube_url"
  | "x_url"
  | "threads_url"
  | "spotify_url"
  | "letterboxd_url"
  | "goodreads_url"
  | "substack_url"
  | "website_url";

export interface SocialLinkConfig {
  key: SocialLinkKey;
  /** Platform name shown as the form label. */
  label: string;
  /** Accessible link text on the profile, e.g. "on Instagram" or "website". */
  linkLabel: string;
  /** Input placeholder. */
  placeholder: string;
}

const DEFAULT_PLACEHOLDER = "@username or full URL";

/** A league can list this many website links (`rec_leagues.website_urls`). */
export const MAX_WEBSITE_LINKS = 10;

export const SOCIAL_LINKS: readonly SocialLinkConfig[] = [
  {
    key: "instagram_url",
    label: "Instagram",
    linkLabel: "on Instagram",
    placeholder: DEFAULT_PLACEHOLDER,
  },
  {
    key: "tiktok_url",
    label: "TikTok",
    linkLabel: "on TikTok",
    placeholder: DEFAULT_PLACEHOLDER,
  },
  {
    key: "youtube_url",
    label: "YouTube",
    linkLabel: "on YouTube",
    placeholder: DEFAULT_PLACEHOLDER,
  },
  {
    key: "x_url",
    label: "X / Twitter",
    linkLabel: "on X",
    placeholder: DEFAULT_PLACEHOLDER,
  },
  {
    key: "threads_url",
    label: "Threads",
    linkLabel: "on Threads",
    placeholder: DEFAULT_PLACEHOLDER,
  },
  {
    key: "spotify_url",
    label: "Spotify",
    linkLabel: "on Spotify",
    placeholder: "Profile URL or username",
  },
  {
    key: "letterboxd_url",
    label: "Letterboxd",
    linkLabel: "on Letterboxd",
    placeholder: "Username or full URL",
  },
  {
    key: "goodreads_url",
    label: "Goodreads",
    linkLabel: "on Goodreads",
    placeholder: "Profile URL or username",
  },
  {
    key: "substack_url",
    label: "Substack",
    linkLabel: "on Substack",
    placeholder: DEFAULT_PLACEHOLDER,
  },
  {
    key: "website_url",
    label: "Website",
    linkLabel: "website",
    placeholder: "https://example.com",
  },
];

export const SOCIAL_LINK_KEYS: readonly SocialLinkKey[] = SOCIAL_LINKS.map(
  ({ key }) => key,
);

export type RecLeagueSocialLinkFields = Partial<
  Record<SocialLinkKey, string | null | undefined>
> & {
  /** Ordered website links; `website_url` mirrors the first entry. */
  website_urls?: readonly string[] | null;
};

/**
 * All website links on the league, in order. Falls back to the legacy single
 * column for payloads that predate `website_urls`.
 */
export function getWebsiteUrls(
  recLeague: Pick<RecLeagueSocialLinkFields, "website_url" | "website_urls">,
): string[] {
  if (recLeague.website_urls?.length) return [...recLeague.website_urls];
  return recLeague.website_url ? [recLeague.website_url] : [];
}

/** Short label for a website link: its hostname without "www.". */
export function getWebsiteLinkLabel(url: string): string {
  try {
    return new URL(toAbsoluteSocialUrl(url)).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** True when the league has at least one social link set. */
export function hasSocialLinks(recLeague: RecLeagueSocialLinkFields): boolean {
  return (
    SOCIAL_LINK_KEYS.some((key) => !!recLeague[key]) ||
    getWebsiteUrls(recLeague).length > 0
  );
}

/** Every non-empty social link URL on the league, in display order. */
export function getSocialLinkUrls(
  recLeague: RecLeagueSocialLinkFields,
): string[] {
  return SOCIAL_LINK_KEYS.flatMap((key) =>
    key === "website_url" ? getWebsiteUrls(recLeague) : [recLeague[key]],
  ).filter((url): url is string => !!url);
}

const HTTP_SCHEME_PATTERN = /^https?:\/\//i;

/**
 * Stored URLs may predate normalization and lack a scheme. Only http(s) is
 * preserved: these values become hrefs, so anything else (javascript:,
 * data:, ...) is pushed through https:// normalization and rendered inert.
 */
export function toAbsoluteSocialUrl(url: string): string {
  return HTTP_SCHEME_PATTERN.test(url) ? url : `https://${url}`;
}
